// =============================================================================
// features/meu-whatsapp/useChannelAiAutoreply.ts — Trava de IA por caixa
// (F43-S09, backend) exposta na inbox pessoal do vendedor (F43-S13).
//
// GET  /api/channels                    — lista (já expõe ai_autoreply_enabled,
//                                          ver ChannelResponseSchema no backend)
// PATCH /api/channels/:id/ai-autoreply  — liga/desliga; 403 para quem não tem
//                                          `channel.ai_autoreply:manage` (só admin)
//
// Por que não reaproveitar `configuracoes/canais/useChannels.ts`: aquele
// schema local (fora de `files_allowed` deste slot) ainda não inclui
// `ai_autoreply_enabled` no parse — adicionar o campo aqui, num hook próprio,
// evita tocar em arquivo fora do escopo do slot e mantém o parse runtime
// (nunca confiar em `any`/cast) sobre exatamente os campos que este hook usa.
//
// Esconder o toggle no front NÃO é o controle de acesso — quem barra é a
// permissão na API (F43-S09/S15). Este hook só busca o dado; a decisão de
// RENDERIZAR o toggle é do componente, via `useAuth().hasPermission(...)`.
// =============================================================================

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { useToast } from '../../components/ui/Toast';
import { ApiError, api } from '../../lib/api';

// ---------------------------------------------------------------------------
// Schemas — só os campos que este hook consome (não o DTO inteiro do canal).
// ---------------------------------------------------------------------------

const ChannelAiAutoreplyEntrySchema = z.object({
  id: z.string().uuid(),
  ai_autoreply_enabled: z.boolean(),
});

const ChannelListSchema = z.object({
  data: z.array(ChannelAiAutoreplyEntrySchema),
});

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const channelAiAutoreplyKeys = {
  byChannel: (channelId: string) => ['channels', 'ai-autoreply', channelId] as const,
};

// ---------------------------------------------------------------------------
// useChannelAiAutoreply — leitura
// ---------------------------------------------------------------------------

/**
 * useChannelAiAutoreply — estado atual da trava de IA de um canal.
 *
 * `enabled: false` (arg) evita a consulta — quem chama decide o gate (mesmo
 * padrão de `useCommercialContext`/`useBriefing`). Aqui o gate real é
 * `hasPermission('channel.ai_autoreply:manage')`: sem a permissão, a rota
 * PATCH nega mesmo se este GET vazasse o valor — mas não há razão para
 * buscar o que não vai ser exibido.
 */
export function useChannelAiAutoreply(channelId: string | null, opts: { enabled: boolean }) {
  return useQuery({
    queryKey: channelAiAutoreplyKeys.byChannel(channelId ?? ''),
    queryFn: async () => {
      const raw = await api.get('/api/channels');
      const { data } = ChannelListSchema.parse(raw);
      return data.find((c) => c.id === channelId) ?? null;
    },
    enabled: opts.enabled && channelId !== null,
    staleTime: 15_000,
  });
}

// ---------------------------------------------------------------------------
// useSetChannelAiAutoreply — mutation
// ---------------------------------------------------------------------------

/**
 * useSetChannelAiAutoreply — liga/desliga a resposta automática do canal.
 *
 * PATCH /api/channels/:id/ai-autoreply — `channel.ai_autoreply:manage`,
 * semeada só para `admin` (F43-S09, migration 0124). 403 vira toast
 * explicativo em vez de erro genérico — mesmo que o botão já esteja
 * escondido para quem não tem a permissão, uma sessão obsoleta (permissão
 * revogada nesta mesma sessão do navegador) ainda pode chegar aqui.
 */
export function useSetChannelAiAutoreply(channelId: string) {
  const qc = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (enabled: boolean) =>
      api.patch(`/api/channels/${encodeURIComponent(channelId)}/ai-autoreply`, { enabled }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: channelAiAutoreplyKeys.byChannel(channelId) });
      toast('Preferência salva.', 'success');
    },
    onError: (err: unknown) => {
      toast(
        err instanceof ApiError && err.status === 403
          ? 'Você não tem permissão para alterar esta configuração.'
          : 'Não foi possível salvar. Tente novamente.',
        'danger',
      );
    },
  });
}
